import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { notifyDocumentExtractionReady } from "AdvisoryManagement/Shared/DocumentExtract/DocumentExtractModal";
import { useLayoutStore } from "AdvisoryManagement/Stores/useLayoutStore";
import { randomUuid } from "AdvisoryManagement/Utils/random";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import {
	ContractStatus,
	getApiUrl,
	postDocumentsDocumentTypeExtractPath,
	type SimplifiedExtractedDocumentResponse,
} from "Infrastructure/Api/Api";
import keys from "../keys";

export const useExtractDocumentMutation = (
	advisoryCaseId: string | undefined,
) => {
	const queryClient = useQueryClient();

	const addJob = useLayoutStore((s) => s.addJob);
	const completeJob = useLayoutStore((s) => s.completeJob);
	const failJob = useLayoutStore((s) => s.failJob);

	return useMutation({
		mutationKey: ["extractDocument"],
		meta: { type: "upload" },
		onMutate: async ({
			fileName,
		}: {
			documentType: string;
			file: File;
			fileName: string;
		}) => {
			const jobId = randomUuid();
			addJob(jobId, { status: "processing", fileName });
			return { jobId };
		},
		mutationFn: async ({
			documentType,
			file,
			fileName,
		}: {
			documentType: string;
			file: File;
			fileName: string;
		}) => {
			const formDataBody = new FormData();
			formDataBody.append("Files", file);
			formDataBody.append("AdvisoryCaseId", advisoryCaseId ?? "");

			const resp = await fetch(
				`${getApiUrl()}${postDocumentsDocumentTypeExtractPath(documentType)}`,
				{
					method: "POST",
					body: formDataBody,
					headers: {
						Authorization: localStorage.getItem(JWT_KEY) ?? "",
					},
				},
			);

			if (!resp.ok) {
				const text = await resp.text().catch(() => "");
				throw new Error(text || "Chyba při extrakci dokumentu");
			}

			const document =
				(await resp.json()) as SimplifiedExtractedDocumentResponse;

			return {
				id: randomUuid(),
				name: fileName,
				content: document,
				uploadDate: new Date().toISOString(),
				type: fileName.split(".").pop()?.toUpperCase() || "Unknown",
			};
		},
		onSuccess: (data, _vars, ctx) => {
			const jobId = ctx!.jobId;

			completeJob(jobId, data.content);
			notifyDocumentExtractionReady(jobId, data.content);

			queryClient.invalidateQueries({
				queryKey: keys.contracts(advisoryCaseId, [ContractStatus.InProgress]),
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: keys.documents,
				exact: false,
			});
		},
		onError: (error, _vars, ctx) => {
			if (ctx?.jobId) {
				failJob(ctx.jobId, (error as Error)?.message);
			}
			toast.error("Chyba při extrakci dokumentu");
		},
	});
};
